import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useEffect, useState } from "react";

import { getTarifasCfe, getTarifasCfeCatalogos, type TarifaCfeRow } from "@/lib/api";

const TARIFA_LABEL: Record<string, string> = {
  GDMTH: "GDMTH · Gran Demanda MT Horaria",
  GDMTO: "GDMTO · Gran Demanda MT Ordinaria",
  DIST: "DIST · Demanda Industrial Subtransmisión",
  DIT: "DIT · Demanda Industrial Transmisión",
  APMT: "APMT · Alumbrado Público MT",
  RAMT: "RAMT · Riego Agrícola MT",
  GDBT: "GDBT · Gran Demanda BT",
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const MES_ABBR = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const NUM = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 6 });

function fmtValor(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? NUM.format(n) : v;
}

function fmtPeriodo(anio: number, mes: number): string {
  return `${MES_ABBR[mes - 1] ?? mes} ${anio}`;
}

function descargarCsv(filename: string, rows: TarifaCfeRow[]): void {
  const header = ["tarifa", "anio", "mes", "division", "cargo", "intervalo", "unidades", "valor"];
  const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([r.tarifa, r.anio, r.mes, r.division, r.cargo, r.intervalo, r.unidades ?? "", r.valor ?? ""]
      .map((c) => esc(String(c))).join(","));
  }
  const blob = new Blob(["﻿" + lines.join("\r\n") + "\r\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function TarifasCfePage() {
  const catQ = useQuery({ queryKey: ["tarifas-cfe", "catalogos"], queryFn: getTarifasCfeCatalogos });

  const [tarifa, setTarifa] = useState("");
  const [desde, setDesde] = useState(""); // "anio-mes"
  const [hasta, setHasta] = useState(""); // "anio-mes"
  const [division, setDivision] = useState(""); // "" = todas

  // Defaults al cargar catálogos: primera tarifa + periodo más reciente en ambos.
  useEffect(() => {
    if (!catQ.data) return;
    if (!tarifa && catQ.data.tarifas[0]) setTarifa(catQ.data.tarifas[0]);
    const p = catQ.data.periodos[0];
    if (p) {
      const v = `${p.anio}-${p.mes}`;
      if (!desde) setDesde(v);
      if (!hasta) setHasta(v);
    }
  }, [catQ.data, tarifa, desde, hasta]);

  const q = useQuery({
    queryKey: ["tarifas-cfe", tarifa, desde, hasta, division],
    queryFn: () => getTarifasCfe({ tarifa, desde, hasta, division: division || undefined }),
    enabled: !!tarifa && !!desde && !!hasta,
  });

  const rows = q.data ?? [];
  const periodos = catQ.data?.periodos ?? [];

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Tarifas CFE</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Cargos de las tarifas de suministro básico (CRE) por división tarifaria y periodo.
              Fuente: <code className="font-mono">public.tarifas_cfe</code> (CFE, actualización mensual).
            </p>
          </div>
          <button
            type="button"
            onClick={() => descargarCsv(`tarifas_cfe_${tarifa}_${desde}_a_${hasta}.csv`, rows)}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 text-sm border rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>

        <div className="pt-3 mt-3 border-t flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Tarifa</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block w-72"
              value={tarifa}
              onChange={(e) => setTarifa(e.target.value)}
            >
              {(catQ.data?.tarifas ?? []).map((t) => (
                <option key={t} value={t}>{TARIFA_LABEL[t] ?? t}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Desde</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            >
              {periodos.map((p) => (
                <option key={`d-${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>
                  {MESES[p.mes - 1]} {p.anio}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Hasta</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            >
              {periodos.map((p) => (
                <option key={`h-${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>
                  {MESES[p.mes - 1]} {p.anio}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">División</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block w-64"
              value={division}
              onChange={(e) => setDivision(e.target.value)}
            >
              <option value="">Todas</option>
              {(catQ.data?.divisiones ?? []).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {catQ.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          Error cargando catálogos: {(catQ.error as Error).message}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Periodo</th>
              {!division && <th className="px-3 py-2 font-medium">División</th>}
              <th className="px-3 py-2 font-medium">Cargo</th>
              <th className="px-3 py-2 font-medium">Int. horario</th>
              <th className="px-3 py-2 font-medium">Unidades</th>
              <th className="px-3 py-2 font-medium text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={division ? 5 : 6} className="px-3 py-8 text-center text-slate-500">Cargando…</td></tr>
            )}
            {q.isError && (
              <tr><td colSpan={division ? 5 : 6} className="px-3 py-8 text-center text-red-600">
                Error: {(q.error as Error).message}
              </td></tr>
            )}
            {!q.isLoading && !q.isError && rows.length === 0 && (
              <tr><td colSpan={division ? 5 : 6} className="px-3 py-8 text-center text-slate-500">
                Sin cargos para esta combinación.
              </td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.anio}-${r.mes}-${r.division}-${r.cargo}-${r.intervalo}-${i}`} className="border-t hover:bg-slate-50">
                <td className="px-3 py-1.5 whitespace-nowrap">{fmtPeriodo(r.anio, r.mes)}</td>
                {!division && <td className="px-3 py-1.5">{r.division}</td>}
                <td className="px-3 py-1.5">{r.cargo}</td>
                <td className="px-3 py-1.5">{r.intervalo === "-" ? "—" : r.intervalo}</td>
                <td className="px-3 py-1.5 text-slate-500">{r.unidades ?? "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmtValor(r.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
